import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { fmtDateTime } from '../api/feeds';
import './RaceDetail.css';
import './RaceCompact.css';
import RaceNavigation from './RaceNavigation';
import RunnerRow from './RunnerRow';
import OddsComparison from './OddsComparison';
import VoiceRecorder from './VoiceRecorder';
import EntityComment from './EntityComment';
import ClassifiedNotes from './ClassifiedNotes';
import { useBetSlip } from '../context/BetSlipContext';
import { Runner, formatOdds, asText } from './raceFormat';

const PRODUCT_TABS = ['Win/Place', 'Parlay'];

interface Race {
  _id: string;
  meeting_name: string;
  race_number: number;
  raw_data: any;
}

interface BetfairMarket {
  market_id: string;
  event_name: string;
  market_name?: string;
  race_number?: number;
  status?: string;
  in_play?: boolean;
  total_matched?: number;
  runners?: any[];
  timestamp?: string;
  number_of_winners?: number;
}

interface GearChange {
  number: number | null;
  runner: string;
  change: string;
}

interface RaceNotes {
  rail: string | null;
  going: string | null;
  gearChanges: GearChange[];
}

interface RaceData {
  race: Race;
  runners: Runner[];
  formData: any[];
  notes?: RaceNotes | null;
  betfairMarket?: BetfairMarket | null;
  betfairPlaceMarket?: BetfairMarket | null;
}

interface RawRunner {
  runnerName?: string;
  runnerNumber?: number;
  riderDriverName?: string;
  riderName?: string;
  driverName?: string;
  jockeyName?: string;
  trainerName?: string;
  barrierNumber?: number;
  handicapWeight?: number;
  allocatedWeight?: number;
  weight?: number | string;
  riderWeight?: number;
  fixedOdds?: {
    returnWin?: number;
    returnPlace?: number;
  };
}

const RaceDetail: React.FC = () => {
  const { meetingName, raceNumber } = useParams();
  const location = useLocation();
  const { status: bfStatus } = useBetSlip();
  const [raceData, setRaceData] = useState<RaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllForm, setShowAllForm] = useState(false);
  const [activeTab, setActiveTab] = useState('Win/Place');
  const [mobileOdds, setMobileOdds] = useState<'WIN'|'PLACE'>('WIN');
  const [commentRefresh, setCommentRefresh] = useState(0);

  useEffect(() => {
    let active = true, pending = false;
    const controller = new AbortController();
    setLoading(true); setRaceData(null); setShowAllForm(false);
    const fetchRaceDetails = async () => {
      if (pending) return;
      pending = true;
      try {
        const selectedDate = new URLSearchParams(location.search).get('date');
        const response = await axios.get(`${API_BASE_URL}/api/races/${encodeURIComponent(meetingName || '')}/${raceNumber}`, {
          params: selectedDate ? {date:selectedDate} : {}, signal:controller.signal, timeout:20000
        });
        if (active) setRaceData(response.data);
      } catch (error) {
        if (active) console.error('Error fetching race details:', error);
      } finally { pending = false; if (active) setLoading(false); }
    };
    fetchRaceDetails();
    const timer = setInterval(fetchRaceDetails, 15000);
    return () => { active = false; controller.abort(); clearInterval(timer); };
  }, [meetingName, raceNumber, location.search]);

  const normalizeWeight = (
    dbWeight: string | undefined,
    rawRunner?: RawRunner
  ): string => {
    // Check if dbWeight is a non-empty string
    if (dbWeight && typeof dbWeight === 'string' && dbWeight.trim().length > 0) {
      return dbWeight;
    }

    const candidateWeights = [
      rawRunner?.allocatedWeight,
      rawRunner?.handicapWeight,
      rawRunner?.riderWeight,
      typeof rawRunner?.weight === 'number' ? rawRunner?.weight : undefined
    ];

    const numericWeight = candidateWeights.find(
      (value): value is number => typeof value === 'number' && value > 0
    );

    if (numericWeight) return String(numericWeight);

    if (typeof rawRunner?.weight === 'string' && rawRunner.weight.trim().length > 0) {
      return rawRunner.weight.trim();
    }

    return '';
  };

  // Group bets data (for future use)
  // const betTypes = [
  //   { name: 'Quinella', key: 'quinella' },
  //   { name: 'Exacta', key: 'exacta' },
  //   { name: 'Duet', key: 'duet' },
  //   { name: 'Trifecta', key: 'trifecta' },
  //   { name: 'First Four', key: 'first_four' },
  // ];

  // Back button target — return to whichever feed we came from (?ref=).
  const backRef = new URLSearchParams(location.search).get('ref');
  const backNav =
    backRef === 'results' ? { to: '/results', label: '← Back to Results' } :
    backRef === 'next' ? { to: '/next-to-jump', label: '← Back to Next To Jump' } :
    backRef === 'futures' ? { to: '/futures', label: '← Back to Futures' } :
    { to: '/', label: '← Back to Dashboard' };

  if (loading) {
    return <div className="loading">Loading race details...</div>;
  }

  if (!raceData || !raceData.race) {
    return (
      <div className="no-data">
        <p>Race not found. Please run the scraper first.</p>
        <Link to={backNav.to}>{backNav.label}</Link>
      </div>
    );
  }

  const { race, runners } = raceData;
  const raceInfo = race.raw_data || {};

  // Some sources prefix the title with the race number already (e.g. Betfair's
  // "R4 1720m Pace M"). Strip a leading "R<number>" so the heading template
  // doesn't render it twice ("R4 R4 1720m Pace M").
  const rawTitle = raceInfo.raceName || raceInfo.race_name || '';
  const raceTitle = rawTitle.replace(
    new RegExp(`^R${race.race_number}\\b\\s*`, 'i'),
    ''
  );

  // Link to a horse profile, carrying this race's context so the horse page's
  // "Back to Race" button can return here even on a direct/bookmarked open.
  const dateParam = new URLSearchParams(location.search).get('date') || '';
  const horseLink = (name: string) =>
    `/horse/${encodeURIComponent(name)}?from=${encodeURIComponent(race.meeting_name)}/${race.race_number}` +
    (dateParam ? `&date=${encodeURIComponent(dateParam)}` : '');

  const rawRunners = (raceInfo.runners as RawRunner[] | undefined) || [];
  const rawRunnerByNumber = new Map<number, RawRunner>(
    rawRunners
      .filter((runner) => !!runner.runnerNumber)
      .map((runner) => [runner.runnerNumber as number, runner])
  );

  const normalizedRunners: Runner[] =
    runners.length > 0
      ? runners.map((runner) => {
          const rawRunner = rawRunnerByNumber.get(runner.horse_number);
          return {
            ...runner,
            horse_name: runner.horse_name || rawRunner?.runnerName || 'Unknown',
            jockey: asText(
              runner.jockey ||
              rawRunner?.riderDriverName ||
              rawRunner?.riderName ||
              rawRunner?.driverName ||
              rawRunner?.jockeyName ||
              rawRunner?.trainerName ||
              '',
              'null'
            ),
            trainer: asText(runner.trainer || rawRunner?.trainerName || '', 'null'),
            barrier: runner.barrier || rawRunner?.barrierNumber || 0,
            weight: normalizeWeight(
              typeof runner.weight === 'string' ? runner.weight : String(runner.weight || ''),
              rawRunner
            ),
            fixed_odd_win: runner.fixed_odd_win || rawRunner?.fixedOdds?.returnWin || 0,
            fixed_odd_place: runner.fixed_odd_place || rawRunner?.fixedOdds?.returnPlace || 0
          };
        })
      : rawRunners.map((runner, index) => ({
          _id: `${race._id}_fallback_${runner.runnerNumber || index + 1}`,
          horse_number: runner.runnerNumber || index + 1,
          horse_name: runner.runnerName || 'Unknown',
          jockey: asText(
            runner.riderDriverName ||
            runner.riderName ||
            runner.driverName ||
            runner.jockeyName ||
            runner.trainerName ||
            '',
            'null'
          ),
          trainer: asText(runner.trainerName || '', 'null'),
          barrier: runner.barrierNumber || 0,
          weight: normalizeWeight('', runner),
          fixed_odd_win: runner.fixedOdds?.returnWin || 0,
          fixed_odd_place: runner.fixedOdds?.returnPlace || 0,
          tote_win: 0,
          tote_place: 0,
          scratched: 0,
          tips: [],
          dividends: []
        }));

  // Market favourites: runners with a real SP, shortest price first
  const marketLeaders = [...normalizedRunners]
    .filter((r) => (r.sp_price ?? 0) > 0)
    .sort((a, b) => (a.sp_price ?? 0) - (b.sp_price ?? 0))
    .slice(0, 4);

  const favourite = marketLeaders[0] ?? null;
  const scratchedCount = normalizedRunners.filter((r) => r.scratched).length;

  return (
    <div className="race-detail">
      <RaceNavigation meeting={race.meeting_name} race={race.race_number} date={dateParam || new Date().toLocaleDateString('en-CA',{timeZone:'Australia/Sydney'})} search={location.search} />
      <header className="race-detail-header">
        <Link to={backNav.to} className="back-button">← {backNav.label}</Link>
        <div className="race-heading-row">
          <div className="race-number-tile" aria-label={`Race ${race.race_number}`}>
            <strong>R{race.race_number}</strong><span>RACING</span>
          </div>
          <div className="race-heading-copy">
            <div className="race-meeting-label">{race.meeting_name}</div>
            <h1>{raceTitle || `${race.meeting_name} · Race ${race.race_number}`}</h1>
            <div className="race-heading-meta">
              {raceInfo.raceStartTime && <span>{fmtDateTime(raceInfo.raceStartTime)}</span>}
              {(raceInfo.raceDistance || raceInfo.race_distance) && <span>{raceInfo.raceDistance || raceInfo.race_distance}m</span>}
              {(raceInfo.trackCondition || raceInfo.track_condition) && <span>{raceInfo.trackCondition || raceInfo.track_condition}</span>}
              <span>{normalizedRunners.filter(r => !r.scratched).length} runners</span>
            </div>
          </div>
        </div>
      </header>

      <div className="race-supplements">
      <details className="race-extra"><summary>Voice notes &amp; race comments</summary><section className="notes-section">
        <VoiceRecorder
          scope="race"
          context={{
            meetingName: race.meeting_name || meetingName,
            raceNumber: race.race_number ?? raceNumber,
            date: dateParam || undefined,
          }}
          title="Race voice notes"
          subtitle={`${race.meeting_name} · R${race.race_number}`}
          onTranscribed={() => setCommentRefresh((n) => n + 1)}
        />

        <EntityComment
          scope="race"
          context={{
            meetingName: race.meeting_name || meetingName,
            raceNumber: race.race_number ?? raceNumber,
            date: dateParam || undefined,
          }}
          title="Race comment"
          refreshKey={commentRefresh}
        />

        <ClassifiedNotes
          mode="race"
          meetingName={race.meeting_name || meetingName || ''}
          raceNumber={race.race_number ?? raceNumber ?? ''}
          date={dateParam || undefined}
        />
      </section></details>

      <details className="race-extra"><summary>Race information &amp; market snapshot</summary><div className="race-info-grid">
        <div className="info-section">
          <div className="info-row">
            <span className="label">Track:</span>
            <span className="value">{race.meeting_name}</span>
          </div>
          <div className="info-row">
            <span className="label">Title:</span>
            <span className="value">{raceTitle || '—'}</span>
          </div>
          <div className="info-row">
            <span className="label">Distance:</span>
            <span className="value">
              {raceInfo.raceDistance || raceInfo.race_distance
                ? `${raceInfo.raceDistance || raceInfo.race_distance}m`
                : '—'}
            </span>
          </div>
          <div className="info-row">
            <span className="label">Race number:</span>
            <span className="value">{race.race_number}</span>
          </div>
          <div className="info-row">
            <span className="label">Condition:</span>
            <span className="value">{(() => {
              const cond = raceInfo.trackCondition || raceInfo.track_condition || '';
              return cond && cond.trim().toUpperCase() !== 'N/A' ? cond : '—';
            })()}</span>
          </div>
          <div className="info-row">
            <span className="label">Full race class:</span>
            <span className="value">{raceInfo.raceClassConditions || raceInfo.race_class_conditions || '—'}</span>
          </div>
        </div>

        <div className="bets-section">
          <h3>Market (by SP)</h3>
          {marketLeaders.length > 0 ? (
            <table className="bets-table">
              <thead>
                <tr>
                  <th>Horse</th>
                  <th>#</th>
                  <th>Form</th>
                  <th>SP</th>
                </tr>
              </thead>
              <tbody>
                {marketLeaders.map((runner) => (
                  <tr key={runner.horse_number}>
                    <td>
                      <Link to={horseLink(runner.horse_name)} className="horse-link">
                        {runner.horse_name}
                      </Link>
                    </td>
                    <td>{runner.horse_number}</td>
                    <td className="form-cell">{runner.pf_form || '—'}</td>
                    <td className="odds">{formatOdds(runner.sp_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No market data available</p>
          )}
        </div>

        <div className="tip-section">
          <h3>Race Snapshot</h3>
          <div className="snapshot">
            <div className="snapshot-row">
              <span className="label">Field size</span>
              <span className="value">{normalizedRunners.length} runners</span>
            </div>
            <div className="snapshot-row">
              <span className="label">Favourite</span>
              <span className="value">
                {favourite
                  ? <>{favourite.horse_name} <strong>{formatOdds(favourite.sp_price)}</strong></>
                  : '—'}
              </span>
            </div>
            <div className="snapshot-row">
              <span className="label">Scratchings</span>
              <span className="value">{scratchedCount > 0 ? scratchedCount : 'None'}</span>
            </div>
            <div className="snapshot-row">
              <span className="label">Betfair market</span>
              <span className="value">
                {raceData.betfairMarket
                  ? <span className="status-active">{raceData.betfairMarket.status || 'OPEN'}</span>
                  : <span className="snapshot-muted">Not available</span>}
              </span>
            </div>
          </div>
        </div>
      </div>

      </details>
      </div>
      <div className="race-card">
        <div className="product-tabs" role="tablist">
          {PRODUCT_TABS.map((t) => (
            <button
              key={t}
              role="tab"
              className={`product-tab${activeTab === t ? ' active' : ''}`}
              aria-selected={activeTab === t}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="race-controls">
          <div className="race-controls-left">
            <button
              className={`ctrl-btn${showAllForm ? ' active' : ''}`}
              onClick={() => setShowAllForm((v) => !v)}
            >
              {showAllForm ? 'Hide All Form' : 'Show All Form'}
            </button>
            <span className="ctrl-compare">
              Compare
              <select className="ctrl-select" defaultValue="Sky Rating">
                <option>Sky Rating</option>
                <option>Form</option>
                <option>Weight</option>
              </select>
            </span>
            <button className="ctrl-pill">Fixed Odds Flucs</button>
            <button className="ctrl-pill">Tote Flucs</button>
          </div>
          <div className={`bf-status-pill ${bfStatus?.configured ? 'ok' : 'off'}`}>
            <span className="bf-dot" />
            {bfStatus?.configured ? (
              <>Betfair connected · <strong>${(bfStatus.account.balance ?? 0).toFixed(2)}</strong></>
            ) : (
              <>Betfair not connected</>
            )}
          </div>
        </div>

        {activeTab === 'Parlay' && <p className="multi-topnote">Select W, P or EW beside each horse to build a multiple. Add races to your slip, then choose Straight or any System sizes.</p>}
        {activeTab !== 'Win/Place' && activeTab !== 'Parlay' ? (
          <div className="product-empty">
            <h3>{activeTab}</h3>
            <p>
              {activeTab} is an exotic bet type (you pick multiple runners/positions). This app’s
              betting backend is the Betfair Exchange, which only offers single Win/Place markets —
              so {activeTab} isn’t available here. Use <strong>Win/Place</strong> to bet.
            </p>
          </div>
        ) : normalizedRunners.length === 0 ? (
          <p className="no-runners-message">
            No runners available for this race in the current database snapshot.
          </p>
        ) : (
          <div className={'runners-table-wrap mobile-odds-' + mobileOdds.toLowerCase()}>
            <div className="mobile-odds-switch" role="group" aria-label="Price market">
              <button type="button" className={mobileOdds === 'WIN' ? 'active' : ''} aria-pressed={mobileOdds === 'WIN'} onClick={() => setMobileOdds('WIN')}>Win odds</button>
              <button type="button" className={mobileOdds === 'PLACE' ? 'active' : ''} aria-pressed={mobileOdds === 'PLACE'} onClick={() => setMobileOdds('PLACE')}>Place odds</button>
            </div>
            <table className="tab-runners">
              <thead>
                <tr className="grp-row">
                  <th colSpan={7} className="grp-blank" />
                  <th className="grp-bf" colSpan={2}>BETFAIR WIN</th>
                  <th className="grp-bf" colSpan={2}>BETFAIR PLACE{raceData.betfairPlaceMarket?.number_of_winners ? ` · ${raceData.betfairPlaceMarket.number_of_winners} places` : ''}</th>
                </tr>
                <tr className="col-row">
                  <th className="th-no">No</th>
                  <th className="th-runner">Runner (Barrier)</th>
                  <th>Form</th>
                  <th>Weight</th>
                  <th title="Core model ranks 1–4">Core rank</th>
                  <th title="Core model fair win odds">Rated price</th>
                  <th title="Betfair back when open; BSP when closed; otherwise recorded decision quote">Market price</th>
                  <th className="col-group-start">Back</th><th>Lay</th>
                  <th className="col-group-start">Back</th><th>Lay</th>
                </tr>
              </thead>
              <tbody>
                {[...normalizedRunners]
                  .sort((a, b) => a.horse_number - b.horse_number)
                  .map((runner) => (
                    <RunnerRow
                      key={`${race.meeting_name}:${race.race_number}:${runner._id}`}
                      runner={runner}
                      horseLink={horseLink}
                      expandAll={showAllForm}
                      placeRunner={raceData.betfairPlaceMarket?.runners?.find((r:any)=>Number(r.selection_id)===Number(runner.bf_selection_id))}
                      market={{
                        marketId: raceData.betfairMarket?.market_id,
                        marketName: raceData.betfairMarket?.market_name,
                        status: raceData.betfairMarket?.status,
                        placeMarketId: raceData.betfairPlaceMarket?.market_id,
                        placeStatus: raceData.betfairPlaceMarket?.status,
                        meetingName: race.meeting_name,
                        raceNumber: race.race_number,
                        date: dateParam || undefined,
                      }}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <details className="race-extra"><summary>Bookmaker comparison</summary>
      {meetingName && raceNumber && (
        <OddsComparison
          meetingName={meetingName}
          raceNumber={raceNumber}
          date={dateParam || undefined}
        />
      )}

      </details>
      {(() => {
        const notes = raceData.notes;
        if (!notes) return null;
        const hasGear = notes.gearChanges && notes.gearChanges.length > 0;
        if (!notes.rail && !notes.going && !hasGear) return null;
        return (
          <div className="race-notes">
            {hasGear && (
              <div className="race-notes-block">
                <h3>Gear Changes</h3>
                <ul className="gear-list">
                  {notes.gearChanges.map((g, i) => (
                    <li key={`${g.number}-${i}`} className="gear-item">
                      <Link to={horseLink(g.runner)} className="gear-runner">
                        {g.number != null ? `${g.number} - ` : ''}{g.runner}
                      </Link>
                      <span className="gear-change"> — {g.change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(notes.rail || notes.going) && (
              <div className="race-notes-block">
                <h3>Track</h3>
                <div className="track-notes">
                  {notes.rail && (
                    <div className="track-note">
                      <span className="track-note-label">Rail</span>
                      <span className="track-note-value">{notes.rail}</span>
                    </div>
                  )}
                  {notes.going && (
                    <div className="track-note">
                      <span className="track-note-label">Going</span>
                      <span className="track-note-value">{notes.going}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default RaceDetail;

